import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { DatasheetLinks } from "@/components/DatasheetLinks";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";
import { SolarScreenShowcase } from "@/components/sections/SolarScreenShowcase";

export const FAQS: FaqItem[] = [
  {
    q: "Vad är skillnaden mellan Solar Screen och 3M Ultra?",
    a: "3M Ultra använder mikrolagerteknik (42 lager i Ultra S800, 375 μm) och används för särskilt utsatta objekt som juvelerare, vapenförvaring och banker. Solar Screen Clear-serien är klassisk PET-baserad säkerhetsfilm i 125–310 μm, CE-märkt enligt EN 12600. För 90 % av butiker, kontor, skolor och vårdmiljöer ger Solar Screen samma praktiska skyddseffekt till ett tydligt lägre kvadratmeterpris. Vi installerar båda och väljer film efter hotbilden.",
  },
  {
    q: "Hur väljer jag rätt tjocklek av Solar Screen säkerhetsfilm?",
    a: "Som tumregel: Clear 4 C (125 μm) för rent splitterskydd i skolor, vårdmiljöer och balkongdörrar i hem. Clear 7 C (210 μm) för standard butik och kontor — mest använd. Clear 8 C (240 μm) för kraftigare inbrottsfördröjning. Clear 12 C (310 μm) för utsatta entrépartier och skyltfönster med risk för riktade inbrott. Vid tveksamhet rekommenderar vi Clear 7 C — bra balans mellan skydd och pris.",
  },
  {
    q: "Är Solar Screen säkerhetsfilm CE-märkt och certifierad?",
    a: "Ja. Hela Clear-serien (4 C, 7 C, 8 C, 12 C) och utvändiga Safe 4 XC är CE-märkta och testade enligt EN 12600 — den europeiska standarden för säkerhetsglas (pendelslagstest). Clear 4 C är klassad 2B2. Filmerna är REACH/RoHS-konforma och har brandklass M1. Tillverkningen i Luxemburg sker enligt ISO 9001 kvalitetsledningssystem.",
  },
  {
    q: "Vad är Solar Screens tillverkargaranti?",
    a: "Clear 4 C och Clear 7 C: 12 års garanti. Clear 8 C och Clear 12 C: 10 års garanti. Utvändiga Safe 4 XC har separata utomhusgarantier enligt Solar Screens 'New guarantees for outdoor products'-policy. Vi som auktoriserad installatör hanterar garantiärenden direkt mot tillverkaren.",
  },
  {
    q: "Hur mycket billigare är Solar Screen jämfört med 3M Ultra?",
    a: "Typiskt 25–40 % lägre kvadratmeterpris i jämförbar tjocklek. För större ytor (butikskedjor, fastighetsbestånd, kontorshus) blir kostnadsskillnaden stor, med bibehållen skyddsfunktion. Båda uppfyller EN 12600. För högriskobjekt (juvelerare, vapen, kontanthantering) rekommenderar vi fortfarande 3M Ultra för maximal styrka per millimeter.",
  },
  {
    q: "Får jag installationsprotokoll som försäkringsbolag accepterar?",
    a: "Ja. Vi levererar fullständigt installationsprotokoll med Solar Screens CE-deklaration, filmklass och tjocklek, monteringsdatum och garantibrev. Svenska försäkringsbolag accepterar Solar Screens EN 12600-dokumentation på samma sätt som motsvarande 3M-underlag — det är den europeiska standarden de utgår från, inte varumärket.",
  },
  {
    q: "Kan ni installera Solar Screen i Skåne och Göteborg?",
    a: "Ja. Vi är auktoriserad Solar Screen-installatör och utför projekt i hela Skåne (Malmö, Lund, Helsingborg, Kristianstad) samt Göteborgsregionen. Vid större rikstäckande uppdrag (butikskedjor, fastighetsbolag) projekterar vi även installation över hela landet med samordnade montörer.",
  },
];

export const Route = createFileRoute("/sakerhetsfilm/solar-screen")({
  head: () => ({
    meta: [
      { title: "Solar Screen Säkerhetsfilm — europeiskt CE-märkt alternativ | BBN Nordic" },
      { name: "description", content: "Solar Screen säkerhetsfilm från Luxemburg — Clear 4 C till 12 C (125–310 μm), CE-märkt enligt EN 12600. Prisvärt europeiskt alternativ till premiumfilm för butik, kontor och fastighet. Auktoriserad installation i hela Sverige." },
      { property: "og:title", content: "Solar Screen Säkerhetsfilm — europeiskt CE-märkt alternativ" },
      { property: "og:description", content: "Europatillverkad säkerhetsfilm i 125–310 μm — CE-märkt enligt EN 12600, 25–40 % lägre kvadratmeterpris än premiumfilm." },
      { property: "og:url", content: "https://bbnnordic.se/sakerhetsfilm/solar-screen" },
      { property: "og:image", content: BBN_PHOTOS.burglaryProtection },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/sakerhetsfilm/solar-screen" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) }],
  }),
  component: () => (
    <ContentPage
      eyebrow="PRODUKTSERIE · EUROPEISK TILLVERKNING"
      title="Solar Screen Säkerhetsfilm"
      subtitle="Solar Screen tillverkar säkerhetsfilm i Luxemburg. Sortimentet är CE-märkt enligt EN 12600 och omfattar fyra klara filmer för invändig montering (125–310 μm) samt utvändig Safe 4 XC. Ett billigare alternativ till dyrare säkerhetsfilm för butik, kontor, skola och fastighet."
      heroImage={BBN_PHOTOS.burglaryProtection}
      breadcrumbs={[{ label: "Säkerhetsfilm", to: "/sakerhetsfilm" }, { label: "Solar Screen" }]}
      sections={[
        {
          eyebrow: "OM TILLVERKAREN",
          heading: "30+ års europeisk tillverkning i Luxemburg.",
          paragraphs: [
            "Solar Screen är ett av Europas mest etablerade fönsterfilmsmärken med tillverkning i Luxemburg sedan tidigt 90-tal. Säkerhetsserien är CE-märkt enligt EN 12600 — den europeiska standarden för säkerhetsglas (pendelslagstest enligt SS-EN 12600) — och tillverkas enligt ISO 9001.",
            "För svenska kunder innebär det kortare leveranskedja inom EU, REACH/RoHS-konform produktion och installationsprotokoll som svenska försäkringsbolag direkt accepterar. Vi är auktoriserad återförsäljare och tar garantiärenden direkt med tillverkaren. Du behöver inte gå via en importör.",
          ],
          bullets: [
            "Tillverkning: Luxemburg (EU) — ISO 9001 kvalitetsledningssystem",
            "Standard: CE-märkt enligt EN 12600 (pendelslag, säkerhetsglas)",
            "Miljö: REACH- och RoHS-konform, brandklass M1",
            "Garanti: 10–12 år direkt från tillverkaren",
            "Sortiment: fyra klara filmer för invändig montering (Clear 4–12 C) + utvändig Safe 4 XC",
            "Distribution: officiell svensk återförsäljare sedan över ett decennium",
          ],
          image: BBN_PHOTOS.shopSecurity,
        },
        {
          eyebrow: "INVÄNDIGT SORTIMENT · CLEAR-SERIEN",
          heading: "Fyra tjocklekar för olika skyddsbehov.",
          paragraphs: [
            "Clear-serien är optiskt klara säkerhetsfilmer som monteras invändigt på glaset. Alla håller ihop splittret vid krossning, möter EN 12600 och blockerar 99 % av UV-strålningen. Tjockleken och draghållfastheten skiljer klasserna åt. Det avgör hur länge filmen fördröjer ett intrångsförsök.",
          ],
          bullets: [
            "Clear 4 C — 125 μm, draghållfasthet 28 kg/cm, EN 12600 klass 2B2. 12 års garanti. Splitterskydd för skola, förskola, vårdmiljö, balkongdörrar.",
            "Clear 7 C — 210 μm, 34 kg/cm. 12 års garanti. Standardval för butik, kontor och offentlig miljö — fördröjer normalt intrång och är vår mest installerade Solar Screen-klass.",
            "Clear 8 C — 240 μm, 40 kg/cm. 10 års garanti. Kraftigare inbrottsfördröjning för butik med exponerade varor och utsatta entrépartier.",
            "Clear 12 C — 310 μm, 61 kg/cm. 10 års garanti. Tjockaste klassen — för juvelerare, optiker, apotek, vapenförvaring och stora skyltfönster i utsatta lägen.",
          ],
          image: BBN_PHOTOS.installationReference,
        },
        {
          eyebrow: "UTVÄNDIGT · SAFE 4 XC",
          heading: "Utvändig film mot vandalism, klotter och glassplitter.",
          paragraphs: [
            "Safe 4 XC monteras på utsidan av glaset och fungerar som ett offerlager: vid klotter, repor eller etsskador byter vi filmen i stället för rutan. Den är samtidigt klassad som säkerhetsfilm och håller ihop glaset vid krossning eller explosion. Lösningsmedelsbeständig och konstruerad för att vara enkel att avlägsna och ersätta.",
          ],
          bullets: [
            "Skyddar mot tags, graffiti och syraetsning",
            "Tål de vanligaste lösningsmedlen som används för klotterborttagning",
            "Säkerhetsklassad — håller ihop glas vid explosion eller slag",
            "Konstruerad för enkel demontering och utbyte vid skada",
            "Brandklass M1, REACH/RoHS-konform",
            "3 års utomhusgaranti från Solar Screen (New Outdoor Guarantees-policy)",
          ],
          image: BBN_PHOTOS.installation1,
        },
        {
          eyebrow: "VAL AV TJOCKLEK",
          heading: "Snabbguide — vilken Clear-klass passar er miljö?",
          bullets: [
            "Privatbostad, balkongdörr, altan → Clear 4 C (125 μm)",
            "Skola, förskola, vårdcentral (splitterskydd) → Clear 4 C (125 μm)",
            "Kontor markplan, mötesrum, reception → Clear 7 C (210 μm)",
            "Butik med exponerade varor → Clear 7 C eller 8 C (210–240 μm)",
            "Juvelerare, optiker, apotek → Clear 12 C (310 μm) + ev. ramförstärkning",
            "Stora skyltfönster i utsatt gatuläge → Clear 12 C (310 μm)",
            "Industri och lager, entrépartier → Clear 7 C (210 μm)",
            "Glas i utsatt gatuläge med klotterrisk → komplettera med Safe 4 XC utvändigt",
          ],
          image: BBN_PHOTOS.installation2,
        },
        {
          eyebrow: "NÄR REKOMMENDERAR VI 3M ULTRA ISTÄLLET?",
          heading: "Så väljer vi mellan Solar Screen och 3M Ultra.",
          paragraphs: [
            "Vi installerar både Solar Screen och 3M Ultra. Vi har inget kommersiellt intresse av att välja det ena märket framför det andra. Vår tumregel är att 3M Ultra (mikrolagerteknik, upp till 375 μm i S800) är förstahandsval när hotbilden inkluderar riktade, planerade inbrott med tunga verktyg — t.ex. juvelerare med högvärdigt lager, vapenförvaring och kontanthantering.",
            "Solar Screen Clear-serien är förstahandsval för i princip alla andra kommersiella objekt: butikskedjor, kontor, skolor, vårdmiljöer och fastighetsbestånd. Du får samma EN 12600-certifiering till 25–40 % lägre kvadratmeterpris. Skyddet räcker för tillfällesinbrott, splitterskydd och försäkringskrav.",
          ],
          bullets: [
            "Välj 3M Ultra: juvelerare, vapen, kontanthantering, ambassadkrav",
            "Välj Solar Screen: butikskedja, kontor, skola, vård, fastighet, bostad",
            "Bägge möter EN 12600 — försäkringsbolagens minimikrav",
          ],
          image: BBN_PHOTOS.installerPortrait,
        },
      ]}
      related={[
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt (3M Ultra)", desc: "Hela sortimentet inkl. 3M Ultra S70–S800 för högriskobjekt." },
        { to: "/solfilm-foretag", label: "Solar Screen Solfilm", desc: "Solfilmsserien från samma tillverkare — sortiment & priser." },
        { to: "/datablad", label: "Tekniska datablad", desc: "PDF-datablad för Clear 4 C–12 C och Safe 4 XC." },
      ]}
      priorityContent={<SolarScreenShowcase />}
    >
      <DatasheetLinks
        items={[
          { name: "Solar Screen Clear 4C", href: "/datablad/solar-screen-clear-4c.pdf" },
          { name: "Solar Screen Clear 7C", href: "/datablad/solar-screen-clear-7c.pdf" },
          { name: "Solar Screen Clear 8C", href: "/datablad/solar-screen-clear-8c.pdf" },
          { name: "Solar Screen Clear 12C", href: "/datablad/solar-screen-clear-12c.pdf" },
          { name: "Solar Screen Safe 4XC", href: "/datablad/solar-screen-safe-4xc.pdf" },
        ]}
      />
      <FaqSection heading="Vanliga frågor om Solar Screen säkerhetsfilm." items={FAQS} />
    </ContentPage>
  ),
});
